import { useState } from 'react'
import { Box, Flex, Grid, Input, VStack } from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { LuArrowLeft, LuSearch, LuPackage } from 'react-icons/lu'
import { toaster, Text, useT } from '@minetrace/ui'
import { brand, themeColors } from '../../theme'
import { recepcionesService } from '../../api/services/recepciones.service'
import { useRequest } from '../../hooks'
import type { LoteValidarQrResponse } from '../../api/models/recepcion.models'
import CharCounter from '../../components/CharCounter'
import { ShipmentRecordFieldLengths } from '../../lib/fieldLengths'

const MotionBox = motion.create(Box)

export default function NuevaRecepcionPage() {
  const colors = themeColors
  const navigate = useNavigate()
  const { t } = useT()
  const [codigo, setCodigo] = useState('')
  const [lote, setLote] = useState<LoteValidarQrResponse | null>(null)

  const buscarLote = useRequest(
    (cod: string) => recepcionesService.buscarLotePorCodigo(cod),
    { showError: true },
  )

  const iniciarRecepcion = useRequest(
    () => {
      if (!lote) throw new Error(t('recepciones.nueva.errors.noLoteSeleccionado'))
      return recepcionesService.iniciar({
        shipmentRecordId: lote.id,
        proveedorRuc: lote.supplierRuc ?? '',
      })
    },
    { showError: true },
  )

  const handleBuscar = async () => {
    const trimmed = codigo.trim()
    if (!trimmed) return
    const result = await buscarLote.execute(trimmed)
    if (result) {
      setLote(result)
    }
  }

  const handleIniciar = async () => {
    if (!lote) return
    const result = await iniciarRecepcion.execute(undefined as never)
    if (result) {
      toaster.success({ description: t('recepciones.nueva.toasts.iniciadaOk') })
      navigate(`/app/recepciones/${result.recepcionId}/validar`, {
        state: { validaciones: result.validaciones },
      })
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleBuscar()
  }

  const tipoPersona = lote?.supplierRuc
    ? lote.supplierRuc.length === 11
      ? t('recepciones.nueva.fields.personaJuridica')
      : t('recepciones.nueva.fields.personaNatural')
    : '-'

  const loteFields = lote
    ? [
        { label: t('recepciones.nueva.fields.codigoLote'), value: lote.batchCode },
        { label: t('recepciones.nueva.fields.proveedor'), value: lote.supplierName ?? '-' },
        { label: t('recepciones.nueva.fields.rucProveedor'), value: lote.supplierRuc ?? '-' },
        { label: t('recepciones.nueva.fields.mineral'), value: lote.mineralTypeName ?? '-' },
        {
          label: t('recepciones.nueva.fields.peso'),
          value: lote.mineralWeight
            ? `${lote.mineralWeight} ${lote.measurementUnitName ?? ''}`.trim()
            : '-',
        },
        { label: t('recepciones.nueva.fields.centroAcopio'), value: lote.collectionCenterName ?? '-' },
        { label: t('recepciones.nueva.fields.conductor'), value: lote.driverName ?? '-' },
        { label: t('recepciones.nueva.fields.placaVehiculo'), value: lote.vehiclePlate ?? '-' },
        { label: t('recepciones.nueva.fields.tipoPersona'), value: tipoPersona },
      ]
    : []

  return (
    <VStack gap={6} alignItems="stretch">
      {/* Breadcrumb */}
      <Flex alignItems="center" gap={3}>
        <Box
          as="button"
          p={2}
          borderRadius="xl"
          cursor="pointer"
          color={colors.textMuted}
          _hover={{ bg: colors.bgHover, color: colors.text }}
          transition="all 0.2s"
          onClick={() => navigate('/app/recepciones')}
        >
          <LuArrowLeft size={20} />
        </Box>
        <Flex alignItems="center" gap={2}>
          <Text
            tid="recepciones.breadcrumb"
            fontSize="sm"
            color={colors.textMuted}
            cursor="pointer"
            _hover={{ color: brand.green }}
            onClick={() => navigate('/app/recepciones')}
          />
          <Text fontSize="sm" color={colors.textDimmed}>
            /
          </Text>
          <Text
            tid="recepciones.nueva.breadcrumb"
            fontSize="sm"
            color={colors.text}
            fontWeight="600"
          />
        </Flex>
      </Flex>

      {/* Search card */}
      <MotionBox
        bg={colors.bgCard}
        border="1px solid"
        borderColor={colors.border}
        borderRadius="2xl"
        p={6}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        boxShadow="none"
      >
        <Flex alignItems="center" gap={2} mb={4}>
          <LuSearch size={18} color={brand.green} />
          <Text
            tid="recepciones.nueva.searchTitle"
            fontSize="lg"
            fontWeight="bold"
            color={colors.text}
          />
        </Flex>
        <Text
          tid="recepciones.nueva.searchSubtitle"
          fontSize="sm"
          color={colors.textSecondary}
          mb={4}
        />

        <Flex gap={3} alignItems="center">
          <Box flex={1}>
            <Input
              placeholder={t('recepciones.nueva.searchPlaceholder')}
              value={codigo}
              onChange={(e) => setCodigo(e.target.value.slice(0, ShipmentRecordFieldLengths.batchCode))}
              onKeyDown={handleKeyDown}
              size="lg"
              w="100%"
              maxLength={ShipmentRecordFieldLengths.batchCode}
            />
            <CharCounter value={codigo} max={ShipmentRecordFieldLengths.batchCode} />
          </Box>
          <Box
            as="button"
            display="flex"
            alignItems="center"
            gap={2}
            px={6}
            py={3}
            bg={brand.primary}
            color="white"
            borderRadius="lg"
            fontSize="sm"
            fontWeight="600"
            cursor="pointer"
            _hover={{ opacity: 0.9 }}
            transition="all 0.2s"
            onClick={handleBuscar}
            opacity={buscarLote.isFetching ? 0.7 : 1}
          >
            <LuSearch size={16} />
            {buscarLote.isFetching
              ? t('recepciones.nueva.searchingButton')
              : t('recepciones.nueva.searchButton')}
          </Box>
        </Flex>
      </MotionBox>

      {/* Lote details */}
      {lote && (
        <MotionBox
          bg={colors.bgCard}
          border="1px solid"
          borderColor={colors.border}
          borderRadius="2xl"
          p={6}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          boxShadow="none"
        >
          <Flex alignItems="center" gap={2} mb={5}>
            <LuPackage size={18} color={brand.green} />
            <Text
              tid="recepciones.nueva.detailTitle"
              fontSize="lg"
              fontWeight="bold"
              color={colors.text}
            />
          </Flex>

          <Grid
            templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }}
            gap={4}
          >
            {loteFields.map((item) => (
              <Box key={item.label}>
                <Text
                  fontSize="10px"
                  textTransform="uppercase"
                  letterSpacing="0.15em"
                  fontWeight="bold"
                  color={colors.textMuted}
                >
                  {item.label}
                </Text>
                <Text fontSize="sm" fontWeight="500" color={colors.text} mt={0.5}>
                  {item.value}
                </Text>
              </Box>
            ))}
          </Grid>

          <Flex mt={6} justifyContent="flex-end">
            <Box
              as="button"
              display="flex"
              alignItems="center"
              gap={2}
              px={6}
              py={3}
              bg={brand.primary}
              color="white"
              borderRadius="lg"
              fontSize="sm"
              fontWeight="600"
              cursor="pointer"
              _hover={{ opacity: 0.9 }}
              transition="all 0.2s"
              onClick={handleIniciar}
              opacity={iniciarRecepcion.isFetching ? 0.7 : 1}
            >
              {iniciarRecepcion.isFetching
                ? t('recepciones.nueva.iniciandoButton')
                : t('recepciones.nueva.iniciarButton')}
            </Box>
          </Flex>
        </MotionBox>
      )}
    </VStack>
  )
}
